import type { TranslationKey } from "@privacy/translations";
import type { FC } from "react";
import { useState } from "react";
import { FormControl, MenuItem, Stack, Tooltip, Button, Select } from "@mui/material";
import { selectClasses } from "@mui/material/Select";
import type { ButtonProps } from "@mui/material/Button";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { useTranslation } from "@privacy/translations";
import {
  type RegistrationById,
  type StatusChangeRequest,
  isUnansweredMandatoryQuestionsError,
  REGISTRATION_STATUS_VARIANTS,
} from "../../api/registrations.types";
import { usePermission } from "../../shared/permissions";
import type { UseMutationResult } from "@tanstack/react-query";
import { RegistrationRequestCompletionDialog } from "./RegistrationDrawer/RegistrationRequestCompletionDialog";
import { UnansweredMandatoryQuestionsDialog } from "../../shared/UnansweredMandatoryQuestionsDialog";
import { StatusOptionLabel } from "../../shared/RegistrationStatusDot";
import { DynamicForm } from "../../dynamicForm";

type RegistrationStatusSelectProps = {
  registration: RegistrationById;
  updateStatusMutation: UseMutationResult<unknown, Error, StatusChangeRequest>;
};

/** This component is used as a select component and as status label depending on the role/permissions. */
export const RegistrationStatusSelect: FC<RegistrationStatusSelectProps> = ({ registration, updateStatusMutation }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const dynamicFormState = DynamicForm.useFormState();
  const [dialogState, setDialogState] = useState<
    | { open: null | "requestCompletion" | "unansweredMandatoryQuestions" }
    | { open: "unsavedChanges"; statusChangeRequest: StatusChangeRequest["status"] }
  >({ open: null });

  const hasReviewPermission = hasPermission("nova-forms-api:registration:review", registration.permissions);

  const hasUpdatePermission = hasPermission("nova-forms-api:registration:update", registration.permissions);

  const handleStatusChange = (newStatus: StatusChangeRequest["status"]) => {
    if (newStatus === "completion") {
      updateStatusMutation.reset();
      return setDialogState({ open: "requestCompletion" });
    }

    updateStatusMutation.mutate(
      { status: newStatus },
      {
        onError: error =>
          isUnansweredMandatoryQuestionsError(error) && setDialogState({ open: "unansweredMandatoryQuestions" }),
      }
    );
  };

  //This has to be done because reviewer role includes update permission and overlaps.
  const isCreator = hasUpdatePermission && !hasReviewPermission;

  // Check if the status select should be shown
  const shouldShowStatusSelectForReviewer = hasReviewPermission && registration.status.status !== "audit";
  const shouldShowStatusSelectForUpdater =
    isCreator &&
    (registration.status.status === "review" ||
      registration.status.status === "done" ||
      (registration.status.status === "audit" && registration.status.auditStatus === "review"));

  if (registration.ownCopy && !registration.ownCopy.acknowledgedAt) return null;

  return (
    <>
      {(shouldShowStatusSelectForReviewer || shouldShowStatusSelectForUpdater) && (
        <Tooltip
          title={
            hasReviewPermission && lacksResponsible(registration) && t("RegistrationGrid.Drawer.NoResponsibleTooltip")
          }
          placement="top-start"
        >
          <FormControl>
            <Select
              disabled={!hasReviewPermission}
              sx={{
                minWidth: 150,
                ...(!hasReviewPermission
                  ? {
                      pointerEvents: "none",
                      [`& .${selectClasses.icon}`]: { display: "none" },
                      [`& .${outlinedInputClasses.notchedOutline}`]: { border: "none" },
                    }
                  : {}),
              }}
              id="status"
              data-testid="status-select"
              size="small"
              value={
                updateStatusMutation.isPending ? updateStatusMutation.variables.status : registration.status.status
              }
              onChange={e => {
                const statusChangeRequest = e.target.value;

                if (dynamicFormState.numberOfUnsavedChanges > 0) {
                  return setDialogState({ open: "unsavedChanges", statusChangeRequest });
                }

                handleStatusChange(statusChangeRequest);
              }}
            >
              {REGISTRATION_STATUS_VARIANTS.map(
                statusVariant =>
                  // If the status is audit, we only show the audit status option as a status label if user isCreator,
                  // otherwise we show action buttons for reviewer.
                  (statusVariant !== "audit" || registration.status.status === "audit") && (
                    <MenuItem
                      data-testid={`status-select-option-${statusVariant}`}
                      key={statusVariant}
                      value={statusVariant}
                      disabled={lacksResponsible(registration) && statusVariant !== "draft"}
                    >
                      <StatusOptionLabel status={statusVariant} />
                    </MenuItem>
                  )
              )}
            </Select>
          </FormControl>
        </Tooltip>
      )}
      {dialogState.open === "requestCompletion" && (
        <RegistrationRequestCompletionDialog
          registration={registration}
          onClose={() => setDialogState({ open: null })}
          updateStatusMutation={updateStatusMutation}
        />
      )}
      {dialogState.open === "unansweredMandatoryQuestions" && (
        <UnansweredMandatoryQuestionsDialog
          onClose={() => setDialogState({ open: null })}
          sections={
            isUnansweredMandatoryQuestionsError(updateStatusMutation.error)
              ? updateStatusMutation.error.unansweredMandatoryQuestionSections
              : []
          }
        />
      )}
      {dialogState.open === "unsavedChanges" && (
        <DynamicForm.UnsavedChangesDialog
          onCancel={() => setDialogState({ open: null })}
          onProceed={() => {
            setDialogState({ open: null });
            handleStatusChange(dialogState.statusChangeRequest);
          }}
        />
      )}
      <RegistrationActionButtons
        registration={registration}
        updateStatusMutation={updateStatusMutation}
        hasReviewPermission={hasReviewPermission}
        isCreator={isCreator}
      />
    </>
  );
};

type RegistrationActionButtonsProps = {
  registration: RegistrationById;
  updateStatusMutation: UseMutationResult<unknown, Error, StatusChangeRequest>;
  hasReviewPermission: boolean;
  isCreator: boolean;
};

const RegistrationActionButtons: FC<RegistrationActionButtonsProps> = ({
  registration,
  updateStatusMutation,
  hasReviewPermission,
  isCreator,
}) => {
  const status = registration.status;
  const isReviewerAndAuditStatusIsReview =
    hasReviewPermission && status.status === "audit" && status.auditStatus === "review";

  const isReviewerAndAuditStatusIsDraftOrCompletion =
    hasReviewPermission &&
    status.status === "audit" &&
    (status.auditStatus === "draft" || status.auditStatus === "completion");

  const isCreatorAndStatusIsDraftOrCompletion =
    isCreator &&
    (status.status === "draft" ||
      status.status === "completion" ||
      // This line below check audit status - "draft" or "completion"
      (status.status === "audit" && (status.auditStatus === "draft" || status.auditStatus === "completion")));

  if (isReviewerAndAuditStatusIsReview) {
    return (
      <Stack direction="row" spacing={1}>
        <SendForDone registration={registration} updateStatusMutation={updateStatusMutation} />
        <SendForCompletion registration={registration} updateStatusMutation={updateStatusMutation} />
      </Stack>
    );
  }

  if (isReviewerAndAuditStatusIsDraftOrCompletion || isCreatorAndStatusIsDraftOrCompletion) {
    return <SendForReview registration={registration} updateStatusMutation={updateStatusMutation} />;
  }

  return null;
};

const lacksResponsible = (registration: RegistrationById) => !registration.responsible;

const noResponsibleTooltipOr = (registration: RegistrationById, tooltipKey: TranslationKey | false | undefined) =>
  lacksResponsible(registration) ? "RegistrationGrid.Drawer.NoResponsibleTooltip" : tooltipKey;

type RegistrationActionButtonProps = {
  registration: RegistrationById;
  updateStatusMutation: UseMutationResult<unknown, Error, StatusChangeRequest>;
};

const SendForDone: FC<RegistrationActionButtonProps> = ({ registration, updateStatusMutation }) => {
  const dynamicFormState = DynamicForm.useFormState();
  const [openDialog, setOpenDialog] = useState<null | "unsavedChanges">(null);
  const disabled = !registration.status.isCompletelyAnswered || lacksResponsible(registration);
  const payload: StatusChangeRequest =
    registration.status.status === "audit" ? { status: "audit", auditStatus: "done" } : { status: "done" };

  return (
    <>
      <ActionButtonUI
        tooltipKey={noResponsibleTooltipOr(
          registration,
          disabled && "RegistrationGrid.Drawer.DoneButton.NotCompletelyAnsweredTooltip"
        )}
        disabled={disabled}
        isLoading={updateStatusMutation.isPending}
        variant="contained"
        onClick={() => {
          if (dynamicFormState.numberOfUnsavedChanges > 0) return setOpenDialog("unsavedChanges");
          updateStatusMutation.mutate(payload);
        }}
        labelKey="RegistrationGrid.Drawer.DoneButton.Label"
      />
      {openDialog === "unsavedChanges" && (
        <DynamicForm.UnsavedChangesDialog
          onCancel={() => setOpenDialog(null)}
          onProceed={() => {
            setOpenDialog(null);
            updateStatusMutation.mutate(payload);
          }}
        />
      )}
    </>
  );
};

const SendForReview: FC<RegistrationActionButtonProps> = ({ updateStatusMutation, registration }) => {
  const dynamicFormState = DynamicForm.useFormState();
  const [openDialog, setOpenDialog] = useState<null | "unsavedChanges" | "unansweredMandatoryQuestions">(null);
  const payload: StatusChangeRequest =
    registration.status.status === "audit" ? { status: "audit", auditStatus: "review" } : { status: "review" };

  const handleStatusChange = () =>
    updateStatusMutation.mutate(payload, {
      onError: error => isUnansweredMandatoryQuestionsError(error) && setOpenDialog("unansweredMandatoryQuestions"),
    });

  return (
    <>
      <ActionButtonUI
        variant="contained"
        size="small"
        tooltipKey={noResponsibleTooltipOr(
          registration,
          registration.nobodyCanReview && "RegistrationGrid.Drawer.NobodyCanReview"
        )}
        disabled={lacksResponsible(registration)}
        isLoading={updateStatusMutation.isPending}
        onClick={() => {
          if (dynamicFormState.numberOfUnsavedChanges > 0) return setOpenDialog("unsavedChanges");
          handleStatusChange();
        }}
        labelKey="RegistrationGrid.Drawer.ReviewButton.Label"
      />
      {openDialog === "unansweredMandatoryQuestions" && (
        <UnansweredMandatoryQuestionsDialog
          onClose={() => setOpenDialog(null)}
          sections={
            isUnansweredMandatoryQuestionsError(updateStatusMutation.error)
              ? updateStatusMutation.error.unansweredMandatoryQuestionSections
              : []
          }
        />
      )}
      {openDialog === "unsavedChanges" && (
        <DynamicForm.UnsavedChangesDialog
          onCancel={() => setOpenDialog(null)}
          onProceed={() => {
            setOpenDialog(null);
            handleStatusChange();
          }}
        />
      )}
    </>
  );
};

const SendForCompletion: FC<RegistrationActionButtonProps> = ({ registration, updateStatusMutation }) => {
  const dynamicFormState = DynamicForm.useFormState();
  const [openDialog, setOpenDialog] = useState<null | "requestCompletion" | "unsavedChanges">(null);
  const disabled = !registration.status.isCompletelyAnswered || lacksResponsible(registration);

  return (
    <>
      <ActionButtonUI
        tooltipKey={noResponsibleTooltipOr(
          registration,
          disabled && "RegistrationGrid.Drawer.CompletionButton.NotCompletelyAnsweredTooltip"
        )}
        disabled={disabled}
        onClick={() => {
          if (dynamicFormState.numberOfUnsavedChanges > 0) return setOpenDialog("unsavedChanges");
          updateStatusMutation.reset();
          setOpenDialog("requestCompletion");
        }}
        labelKey="RegistrationGrid.Drawer.CompletionButton.Label"
      />

      {openDialog === "requestCompletion" && (
        <RegistrationRequestCompletionDialog
          registration={registration}
          onClose={() => setOpenDialog(null)}
          updateStatusMutation={updateStatusMutation}
        />
      )}

      {openDialog === "unsavedChanges" && (
        <DynamicForm.UnsavedChangesDialog
          onCancel={() => setOpenDialog(null)}
          onProceed={() => {
            updateStatusMutation.reset();
            setOpenDialog("requestCompletion");
          }}
        />
      )}
    </>
  );
};

type ActionButtonProps = {
  isLoading?: boolean;
  labelKey: TranslationKey;
  tooltipKey?: TranslationKey | false;
} & Omit<ButtonProps, "children">;

const ActionButtonUI: FC<ActionButtonProps> = ({ labelKey, tooltipKey, isLoading, ...props }) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={tooltipKey && t(tooltipKey)} placement="bottom-start">
      <span>
        <Button loading={isLoading} variant="outlined" size="small" {...props}>
          {t(labelKey)}
        </Button>
      </span>
    </Tooltip>
  );
};
